import { useCallback } from 'react';
import { ToolClickEventArgs } from '@thive/canvas';
import { FDDM_AREA_TAGS, FDDM_TAG } from '@/modules/floorplanDesign/constants';
import { useFlowClick } from '@/modules/canvas/hooks/tool/useFlowClick';
import { useReferenceScaleClick } from '@/modules/canvas/hooks/tool/useReferenceScaleClick';
import { useDistconClick } from '@/modules/connections/hooks/useDistconClick';
import { useActiveFlowStore } from '@/modules/flowsV2/store/useActiveFlowStore';
import { useConnectionClick } from '@/modules/connections/hooks/useConnectionClick';
import { intersect } from '@/modules/common/helpers/array';
import { useChooseClick } from '@/modules/canvas/hooks/tool/useChooseClick';
import { ToolId } from '@modules/toolbar/types';
import { useTemplateClick } from '@/modules/canvas/hooks/tool/useTemplateClick';
import { useActiveDistconStore } from '@/modules/connections';
import { logger } from '@common/logging/logger';

export const useToolClick = () => {
  const { flowClick } = useFlowClick();
  const { distconClick } = useDistconClick();
  const { connectionClick } = useConnectionClick();
  const { chooseClick } = useChooseClick();
  const { templateClick } = useTemplateClick();
  const { scaleClick } = useReferenceScaleClick();
  const currentFlowSourceId = useActiveFlowStore((state) => state.currentFlowSourceFlowStopType);
  const currentDistconSource = useActiveDistconStore((state) => state.source);

  const toolClick = useCallback(
    (activeTool: ToolId, e: ToolClickEventArgs) => {
      switch (activeTool) {
        case 'consolidation_flow':
        case 'transport_flow':
        case 'external_flow': {
          const { element, position } = e;
          // If we a source id, it is the second click
          const secondClick = currentFlowSourceId !== null;
          if (!element) {
            return;
          }
          if (!position) {
            logger.error(
              'Something went wrong. Expected a position to be provided by canvas ' +
                'ToolClickEventArgs.',
            );
            break;
          }
          flowClick(element, position, secondClick);

          break;
        }
        case 'distcon': {
          const { element, position } = e;
          // If we have a source, it is the second click
          const secondClick = currentDistconSource !== null;

          if (!element) {
            return;
          }
          if (!position) {
            logger.error(
              'Something went wrong. Expected a position to be provided by canvas ' +
                'ToolClickEventArgs.',
            );
            break;
          }

          distconClick(element, position, secondClick);

          return;
        }
        case 'scale_reference': {
          const { position } = e;

          if (!position) {
            logger.error(
              'Something went wrong. Expected a clicked position to be provided by the ' +
                'canvas event',
            );
            break;
          }

          scaleClick(position);

          return;
        }
        case 'cursor':
          if (!e.element) return;
          if (!e.element.tags) return;
          if (!e.element.tags.includes(FDDM_TAG.INTER_AREA_CONNECTION)) return;

          connectionClick(e.element);
          break;
        case 'choose':
          if (!e.element) return;
          if (!e.element.tags) return;
          if (!intersect(e.element.tags, FDDM_AREA_TAGS)) return;

          chooseClick(e.element);
          break;
        case 'lassie':
          templateClick(e.position, 'LASSIE');
          break;
        case 'lassie_mini':
          templateClick(e.position, 'LASSIE_MINI');
          break;
        case 'shuttle':
          templateClick(e.position, 'SHUTTLE');
          break;
        case 'hik':
          templateClick(e.position, 'HIK');
          break;
        default:
          break;
      }
    },
    [
      chooseClick,
      connectionClick,
      currentDistconSource,
      currentFlowSourceId,
      distconClick,
      flowClick,
      templateClick,
      scaleClick,
    ],
  );

  return { toolClick };
};
